import { useState } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import OverviewTab from './OverviewTab';
import UserEngagementTab from './UserEngagementTab';
import BusinessPerformanceTab from './BusinessPerformanceTab';
import RevenueTab from './RevenueTab';
import { AdminAnalyticsData } from '../../../types/admin';
import BarChartIcon from '@mui/icons-material/BarChart';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

interface AnalyticsTabsProps {
  data: AdminAnalyticsData;
  loading?: boolean;
  onExport: (tabName: string) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

/**
 * AnalyticsTabs - Main tab navigation for analytics
 *
 * Provides 4 tabs:
 * 1. Overview - Key metrics and trends
 * 2. User Engagement - DAU, MAU, cohort retention, feature usage
 * 3. Business Performance - Business metrics and top performers
 * 4. Revenue Analytics - MRR, ARR, revenue trends
 */
export default function AnalyticsTabs({ data, loading, onExport }: AnalyticsTabsProps) {
  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Box>
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        aria-label="analytics tabs"
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
        }}
      >
        <Tab
          icon={<BarChartIcon />}
          iconPosition="start"
          label="Overview"
          id="analytics-tab-0"
          aria-controls="analytics-tabpanel-0"
        />
        <Tab
          icon={<PeopleIcon />}
          iconPosition="start"
          label="User Engagement"
          id="analytics-tab-1"
          aria-controls="analytics-tabpanel-1"
        />
        <Tab
          icon={<BusinessIcon />}
          iconPosition="start"
          label="Business Performance"
          id="analytics-tab-2"
          aria-controls="analytics-tabpanel-2"
        />
        <Tab
          icon={<AttachMoneyIcon />}
          iconPosition="start"
          label="Revenue Analytics"
          id="analytics-tab-3"
          aria-controls="analytics-tabpanel-3"
        />
      </Tabs>

      <TabPanel value={currentTab} index={0}>
        <OverviewTab data={data} loading={loading} onExport={() => onExport('overview')} />
      </TabPanel>

      <TabPanel value={currentTab} index={1}>
        <UserEngagementTab data={data} loading={loading} />
      </TabPanel>

      <TabPanel value={currentTab} index={2}>
        <BusinessPerformanceTab data={data} loading={loading} />
      </TabPanel>

      <TabPanel value={currentTab} index={3}>
        <RevenueTab data={data} loading={loading} onExport={() => onExport('revenue')} />
      </TabPanel>
    </Box>
  );
}
